import MaterialIcon from '../MaterialIcon';
import { formatNumber, formatRelativeTime } from '../../utils/formatters';

function toneClasses(status = 'healthy', stale = false) {
  if (stale) {
    return 'border-[#fd6c00]/40 bg-[#2b1403] text-[#ffb692]';
  }
  if (status === 'warning') {
    return 'border-[#fd6c00]/40 bg-[#2b1403] text-[#ffb692]';
  }
  if (status === 'critical' || status === 'offline') {
    return 'border-[#93000a]/60 bg-[#2a1112] text-[#ffdad6]';
  }
  return 'border-[#f1c100]/20 bg-[#14120a] text-[#f7d88a]';
}

export default function CommandAnalyticsStrip({
  summary,
  title = 'Cross-product telemetry',
  description = 'Unified analytics from Shimbirr, PMS, Taban Food, and Taban Website.',
  onRefresh,
  refreshing = false,
  compact = false,
  deskBasePath = '/desk',
}) {
  if (!summary) return null;

  return (
    <section className="rounded-[1.8rem] border border-[#4e4632] bg-[#1a160b] p-5 shadow-[0_18px_50px_rgba(0,0,0,0.28)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-[#f1c100]">
            <span className="h-2 w-2 rounded-full bg-[#f1c100]" />
            Live command analytics
          </div>
          <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">{title}</h2>
          <p className="mt-2 text-sm leading-6 text-[#d2c5ab]">{description}</p>
          <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#9a9078]">
            Last update {summary.lastUpdatedAt ? formatRelativeTime(summary.lastUpdatedAt) : 'not recorded'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-full border border-[#4e4632] bg-[#111111] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#d2c5ab]">
            {formatNumber(summary.sources?.length || 0)} sources
          </div>
          <div className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] ${toneClasses(summary.freshness?.status, summary.freshness?.staleSourceCount > 0)}`}>
            {summary.freshness?.staleSourceCount > 0
              ? `${formatNumber(summary.freshness?.staleSourceCount || 0)} stale`
              : 'fresh'}
          </div>
          {onRefresh ? (
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 rounded-full border border-[#f1c100]/40 bg-[#ffcc00] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#3d2f00] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <MaterialIcon name={refreshing ? 'sync' : 'refresh'} className={refreshing ? 'animate-spin text-sm' : 'text-sm'} />
              {refreshing ? 'Refreshing' : 'Refresh'}
            </button>
          ) : null}
        </div>
      </div>

      {!compact && Array.isArray(summary.summaryCards) && summary.summaryCards.length ? (
        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          {summary.summaryCards.map((card) => (
            <div key={card.key} className="rounded-[1.2rem] border border-[#4e4632] bg-[#111111] px-4 py-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#9a9078]">
                {card.label}
              </p>
              <p className="mt-3 text-2xl font-black text-white">{formatNumber(card.value || 0)}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-5 grid grid-cols-1 gap-3 xl:grid-cols-4">
        {(summary.sources || []).map((source) => (
          <div
            key={source.productKey}
            className={`rounded-[1.2rem] border px-4 py-4 ${toneClasses(source.status, source.stale)}`}
          >
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-bold text-white">{source.productLabel}</p>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]">
                {source.mode}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-[0.18em]">
              <span>{`Telemetry ${source.status || 'pending'}`}</span>
              <span>{`License ${source.licenseStatus || 'unknown'}`}</span>
            </div>
            <p className="mt-3 text-xs text-[#d2c5ab]">
              {source.generatedAt ? `Generated ${formatRelativeTime(source.generatedAt)}` : 'No snapshot yet'}
            </p>
            {source.links?.adminUrl || source.links?.renewalsUrl || (deskBasePath && source.productKey !== 'shimbirr') ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {deskBasePath && source.productKey !== 'shimbirr' ? (
                  <a
                    href={`${deskBasePath}/${source.productKey}`}
                    className="inline-flex items-center gap-2 rounded-full border border-[#4e4632] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#d2c5ab] transition hover:bg-[#1f1f1f]"
                  >
                    <MaterialIcon name="dashboard" className="text-xs" />
                    Desk
                  </a>
                ) : null}
                <a
                  href={source.links?.adminUrl || source.links?.renewalsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-[#f1c100]/35 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#f7d88a] transition hover:bg-[#f1c100] hover:text-[#241a00]"
                >
                  <MaterialIcon name="open_in_new" className="text-xs" />
                  Open source
                </a>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}
